import { css } from '@emotion/react'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import { observer } from 'mobx-react'

import { default as Checkbox } from '@/component/atoms/Checkbox/_Checkbox'
import SubTab from '@/component/molecules/SubTab/SubTab'
import AssignGroupStudentModal from '@/contentComponents/modal/AssignGroupStudentModal'
import type { Student } from '@/dto'
import commonRepo from '@/repository/common.repo'
import modalService from '@/service/modal.service'
import type { PreviousExclusionScope } from '@/types/makeWorksheet'

const SCOPE_VALUES: PreviousExclusionScope[] = [
  'ACADEMY_CREATED',
  'TEACHER_CREATED',
  'STUDENT_ASSIGNED',
]

const getScopeInfo = (): Record<
  PreviousExclusionScope,
  { label: string; description: string }
> => ({
  ACADEMY_CREATED: {
    label: commonRepo.agencyNameInKor,
    description: `${commonRepo.agencyNameInKor}에서 최근 30일간 사용한 문제가 모두 제외됩니다.`,
  },
  TEACHER_CREATED: {
    label: '선생님',
    description: '선생님께서 최근 30일간 사용한 문제가 모두 제외됩니다.',
  },
  STUDENT_ASSIGNED: {
    label: '학생',
    description: `선택한 학생에게 최근 30일간 ${tJosa('출제')}된 문제가 모두 제외됩니다.`,
  },
})

type ChangePayload = {
  excludePrevious: boolean
  previousExclusionScope?: PreviousExclusionScope
  studentIds?: Student['id'][]
}

/** '학생 기준' 제외인데 대상 학생을 선택하지 않은 미완성 상태 — 다음 단계로 넘어갈 수 없다. */
export const isStudentScopeIncomplete = (filter: {
  excludePrevious: boolean
  previousExclusionScope?: PreviousExclusionScope | null
  studentIds?: Student['id'][] | null
}): boolean =>
  filter.excludePrevious &&
  filter.previousExclusionScope === 'STUDENT_ASSIGNED' &&
  !filter.studentIds?.length

/** 제외 여부·대상 학생을 제외 기준(scope) 하나에서 일관되게 파생시킨다. */
const buildChange = (
  scope: PreviousExclusionScope | undefined,
  studentIds?: Student['id'][],
): ChangePayload => ({
  excludePrevious: scope !== undefined,
  previousExclusionScope: scope,
  studentIds: scope === 'STUDENT_ASSIGNED' ? studentIds : undefined,
})

type Props = {
  /** undefined 이면 미선택(체크 해제), 값이 있으면 선택(체크) — 제외 여부의 SSOT */
  previousExclusionScope?: PreviousExclusionScope
  studentIds?: Student['id'][]
  onChange: (next: ChangePayload) => void
}

/**
 * 기존 출제 문제 제외 체크박스 + 제외 기준(학원/선생님/학생) 선택을 묶은 공용 옵션 컴포넌트.
 * 제외 여부는 previousExclusionScope 의 유무로 표현한다(undefined=해제, 값=선택).
 */
const ExcludePreviousOption = ({ previousExclusionScope, studentIds, onChange }: Props) => {
  return (
    <div css={S.container}>
      <div css={S.row}>
        <Checkbox
          text={`기존 ${tJosa('출제')} 문제 제외`}
          checked={previousExclusionScope !== undefined}
          onChange={(checked) =>
            onChange(
              buildChange(
                checked ? previousExclusionScope ?? 'ACADEMY_CREATED' : undefined,
                studentIds,
              ),
            )
          }
        />
        <span css={S.notice}>같은 문제, 다시 {tJosa('출제')}되지 않아요</span>
      </div>
      {previousExclusionScope !== undefined && (
        <ScopeBox
          previousExclusionScope={previousExclusionScope}
          studentIds={studentIds}
          onChange={onChange}
        />
      )}
    </div>
  )
}

export default observer(ExcludePreviousOption)

/**
 * 제외 기준(학원/선생님/학생) 선택 패널. 학생 기준은 AssignGroupStudentModal 로 대상 학생을 고른다.
 */
const ScopeBox = observer(
  ({
    previousExclusionScope,
    studentIds,
    onChange,
  }: {
    previousExclusionScope: PreviousExclusionScope
    studentIds?: Student['id'][]
    onChange: (next: ChangePayload) => void
  }) => {
    const openStudentSelectModal = () =>
      modalService.openModal(
        <AssignGroupStudentModal
          title={`기존 ${tJosa('출제')} 문제를 제외할 학생 선택`}
          warnDeleteAssigned={false}
          assignedLists={[studentIds?.map((studentId) => ({ studentId })) ?? []]}
          onComplete={(students) =>
            onChange(
              buildChange(
                'STUDENT_ASSIGNED',
                students.map(({ studentId }) => studentId),
              ),
            )
          }
        />,
      )

    const scopeInfo = getScopeInfo()

    return (
      <div css={S.box}>
        <SubTab
          css={S.tab}
          value={previousExclusionScope}
          onChangeTab={(value) =>
            onChange(buildChange(value as PreviousExclusionScope, studentIds))
          }
        >
          {SCOPE_VALUES.map((scope) => (
            <SubTab.Item
              key={scope}
              css={S.tabItem}
              value={scope}
              onClick={scope === 'STUDENT_ASSIGNED' ? openStudentSelectModal : undefined}
            >
              {scopeInfo[scope].label}
              {scope === 'STUDENT_ASSIGNED' && previousExclusionScope === 'STUDENT_ASSIGNED' && (
                <span css={studentIds && studentIds.length > 0 ? S.statusDone : S.statusRequired}>
                  {studentIds && studentIds.length > 0 ? '선택완료' : '선택필요'}
                </span>
              )}
            </SubTab.Item>
          ))}
        </SubTab>
        <p css={S.description}>{scopeInfo[previousExclusionScope].description}</p>
      </div>
    )
  },
)

const S = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: 10px;
  `,
  row: css`
    display: flex;
    align-items: center;

    /* 부모의 광범위한 label margin 리셋에도 체크박스-라벨 간격을 유지한다. */
    .with-text {
      margin-left: 12px;
    }
  `,
  notice: css`
    position: relative;
    margin-left: 10px;
    padding: 2px 4px;
    border-radius: var(--Radius-100);
    background: var(--Colors-Orange-200);
    color: var(--Colors-Orange-600);
    font-size: 9px;
    line-height: 12px;
    font-weight: 700;
    white-space: nowrap;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: -3px;
      transform: translateY(-50%);
      border-style: solid;
      border-width: 3px 3px 3px 0;
      border-color: transparent var(--Colors-Orange-200) transparent transparent;
    }
  `,
  box: css`
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 460px;
    padding: 12px 16px;
    border-radius: var(--Radius-200);
    background: var(--Colors-Gray-100);
  `,
  tab: css`
    width: 100%;
  `,
  tabItem: css`
    flex: 1;
    /* SubTab Container 가 align-items: center 라 기본적으로 탭이 텍스트 높이로만 잡힌다.
       컨테이너 높이를 꽉 채우도록 stretch 시킨다. */
    align-self: stretch;

    /* 부모의 광범위한 label 높이 규칙(예: .extra-options label)을 이기기 위해 && 로 명시도를 올린다. */
    && label {
      width: 100%;
      height: 100%;
      justify-content: center;
    }
  `,
  description: css`
    font-size: 12px;
    line-height: 18px;
    color: var(--Colors-Gray-600);
    text-align: center;
  `,
  statusRequired: css`
    font-size: 9px;
    line-height: 12px;
    color: var(--Colors-Red-400);
  `,
  statusDone: css`
    font-size: 9px;
    line-height: 12px;
    color: var(--Colors-Sky-400);
  `,
}
